/**
 * Gerendertes Markdown — read-only Preview.
 *
 * Vorprozessieren: `[[Wikilink]]` → `[Wikilink](#wl:Wikilink)`, damit
 * react-markdown sie als normale Links rendert. Im components.a-Slot fangen
 * wir die `#wl:`-prefixed-Links wieder ab und bauen einen onClick-Handler.
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Props {
  source: string;
  onWikilinkClick: (target: string) => void;
}

const WIKILINK_RE = /\[\[([^\]|#]+)(?:#([^\]|]+))?(?:\|([^\]]+))?\]\]/g;

function preprocess(md: string): string {
  return md.replace(WIKILINK_RE, (_full, target, anchor, label) => {
    const display = (label || target).trim();
    const t = encodeURIComponent(target.trim());
    const a = anchor ? "#" + encodeURIComponent(anchor.trim()) : "";
    return `[${display}](#wl:${t}${a})`;
  });
}

export function MarkdownPreview({ source, onWikilinkClick }: Props) {
  return (
    <div className="h-full overflow-y-auto px-6 py-4">
      <article
        className="prose prose-invert prose-zinc max-w-3xl
          prose-headings:font-semibold prose-headings:text-zinc-100
          prose-h1:text-3xl prose-h1:mt-0 prose-h1:mb-4
          prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-3
          prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-2
          prose-p:text-zinc-200 prose-p:leading-relaxed
          prose-strong:text-white prose-strong:font-semibold
          prose-em:text-zinc-300
          prose-code:text-amber-300 prose-code:bg-zinc-900 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
          prose-pre:bg-zinc-900 prose-pre:border prose-pre:border-zinc-800
          prose-blockquote:border-l-emerald-700 prose-blockquote:text-zinc-400 prose-blockquote:italic
          prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
          prose-li:text-zinc-200
          prose-table:text-sm
          prose-th:border-zinc-800 prose-td:border-zinc-800 prose-th:bg-zinc-900
          prose-hr:border-zinc-800"
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children, ...rest }) => {
              if (typeof href === "string" && href.startsWith("#wl:")) {
                const rest_ = href.slice(4);
                const [tEnc] = rest_.split("#");
                const target = decodeURIComponent(tEnc);
                return (
                  <button
                    onClick={() => onWikilinkClick(target)}
                    className="text-emerald-400 hover:text-emerald-300 underline decoration-emerald-700 decoration-dotted hover:decoration-solid"
                    title={`Open: ${target}`}
                  >
                    {children}
                  </button>
                );
              }
              return (
                <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
                  {children}
                </a>
              );
            },
            // Checkbox-Items werden interaktiv (read-only, aber sichtbar)
            input: (props) => {
              if (props.type === "checkbox") {
                return (
                  <input
                    {...props}
                    disabled
                    className="mr-2 h-3.5 w-3.5 align-middle accent-emerald-500"
                  />
                );
              }
              return <input {...props} />;
            },
          }}
        >
          {preprocess(source)}
        </ReactMarkdown>
      </article>
    </div>
  );
}
